(() => {
  "use strict";

  if (window.GuruProductCard?.initialized) return;

  const CARD_SELECTOR = "[data-guru-product-card]";
  const ACTION_SELECTOR = "[data-guru-product-card-action]";
  const FORM_SELECTOR = "[data-guru-product-card-form] form";
  const MODAL_SELECTOR = "[data-guru-quick-buy-modal]";
  const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
  const COARSE_POINTER = "(hover: none), (pointer: coarse)";
  const MEMBER_PRICE_SOURCE = "shopify-discount-allocation";
  const MAX_COMMERCE_OPERATIONS = 32;
  const MAX_COMMERCE_IDENTITY_LENGTH = 8192;
  const CART_ADD_TIMEOUT = 10000;
  const CART_ADD_TIMEOUT_MIN = 50;
  const CART_ADD_TIMEOUT_MAX = 15000;
  const CART_ERROR_BODY_TIMEOUT = 250;
  const CART_ERROR_BODY_TIMEOUT_MIN = 25;
  const CART_ERROR_BODY_TIMEOUT_MAX = 1000;
  const CART_ERROR_DESCRIPTION_MAX = 500;
  const PREDICTIVE_INVALIDATE_EVENT = "guru:predictive-search:invalidate";
  // Unknown POST outcomes deliberately have no time-based expiry. A late commit
  // has no safe wall-clock deadline, so expiring an unresolved entry could
  // authorize a duplicate POST. The hard cap bounds memory instead; completed
  // and definitive 422 operations are removed immediately.
  const commerceOperationLedger = new Map();
  const commerceSubmissionLocks = new Map();
  const quickBuyOperations = new WeakMap();
  const predictiveQuickBuyOperations = new WeakMap();
  let quickBuyOperationSequence = 0;
  let renderedCardSequence = 0;

  const readConfig = () => {
    try {
      const value = JSON.parse(
        document.getElementById("guru-product-card-config")?.textContent || "{}",
      );
      return value && typeof value === "object" ? value : {};
    } catch (_error) {
      return {};
    }
  };

  const config = readConfig();
  const translations = config.translations && typeof config.translations === "object"
    ? config.translations
    : {};

  const translate = (key, replacements = {}) => {
    let value = typeof translations[key] === "string" ? translations[key] : "";
    Object.entries(replacements).forEach(([token, replacement]) => {
      value = value.replaceAll(`__${token.toUpperCase()}__`, String(replacement));
    });
    return value;
  };

  const positiveSafeInteger = (value) => {
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : null;
  };

  const normalizeQuantityRule = (value) => {
    if (!value || typeof value !== "object") return null;
    const minimum = positiveSafeInteger(value.minimum);
    const increment = positiveSafeInteger(value.increment);
    const maximum = value.maximum === null || value.maximum === undefined || value.maximum === ""
      ? null
      : positiveSafeInteger(value.maximum);
    if (
      !minimum
      || !increment
      || (value.maximum !== null && value.maximum !== undefined && value.maximum !== "" && !maximum)
      || (maximum !== null && maximum < minimum)
    ) return null;
    return { minimum, increment, maximum };
  };

  const quantityAllowed = (quantity, rule) => Boolean(
    rule
    && Number.isSafeInteger(quantity)
    && quantity >= rule.minimum
    && (quantity - rule.minimum) % rule.increment === 0
    && (rule.maximum === null || quantity <= rule.maximum)
  );

  const quantityRuleForInput = (input) => normalizeQuantityRule({
    minimum: input?.dataset.guruQuantityMin,
    increment: input?.dataset.guruQuantityIncrement,
    maximum: input?.dataset.guruQuantityMax || null,
  });

  const normalizeQuantityPriceBreaks = (value, rule, priceAmount, linePriceAmount) => {
    if (
      !rule
      || !Array.isArray(value)
      || !Number.isSafeInteger(priceAmount)
      || priceAmount < 0
      || !Number.isSafeInteger(linePriceAmount)
      || linePriceAmount < 0
    ) return null;
    const normalized = [];
    let previousMinimum = 0;
    let applicableUnitPrice = priceAmount;
    for (const priceBreak of value) {
      const minimumQuantity = positiveSafeInteger(priceBreak?.minimumQuantity);
      const price = Number(priceBreak?.price);
      if (
        !minimumQuantity
        || minimumQuantity <= previousMinimum
        || !Number.isSafeInteger(price)
        || price < 0
      ) return null;
      normalized.push({ minimumQuantity, price });
      previousMinimum = minimumQuantity;
      if (minimumQuantity <= rule.minimum) applicableUnitPrice = price;
    }
    return linePriceAmount === applicableUnitPrice * rule.minimum ? normalized : null;
  };

  const isVisible = (element) => Boolean(
    element
    && element.isConnected
    && !element.closest("[hidden]")
    && element.getClientRects().length,
  );

  const setCardError = (card, message = "") => {
    const error = card?.querySelector("[data-guru-product-card-error]");
    if (error) {
      error.classList.remove("is-success");
      error.setAttribute("role", "alert");
      error.removeAttribute("aria-live");
      error.textContent = message;
      error.hidden = !message;
    }

    const globalError = document.getElementById("error-announcement");
    if (message && globalError) globalError.textContent = message;
  };

  const escapeSelector = (value) => (
    window.CSS?.escape ? CSS.escape(value) : String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&")
  );

  const controlsForModal = (modal) => (
    modal?.id
      ? Array.from(document.querySelectorAll(`[aria-controls="${escapeSelector(modal.id)}"]`))
      : []
  );

  const setModalControlsState = (modal, { expanded, busy } = {}) => {
    controlsForModal(modal).forEach((control) => {
      if (expanded !== undefined) control.setAttribute("aria-expanded", String(expanded));
      if (busy === true) control.setAttribute("aria-busy", "true");
      if (busy === false) control.removeAttribute("aria-busy");
    });
  };

  const finishReducedMotionAnimations = (modal) => {
    if (!window.matchMedia(REDUCED_MOTION).matches) return;
    [modal, modal?.shadowRoot]
      .filter(Boolean)
      .flatMap((root) => root.getAnimations?.({ subtree: true }) || [])
      .forEach((animation) => {
        try {
          if (animation.effect?.getTiming?.().iterations === Infinity) animation.cancel();
          else animation.finish();
        } catch (_error) {
          animation.cancel();
        }
      });
  };

  const settleReducedMotionAnimations = (modal) => {
    if (!window.matchMedia(REDUCED_MOTION).matches) return;
    finishReducedMotionAnimations(modal);
    queueMicrotask(() => finishReducedMotionAnimations(modal));
    requestAnimationFrame(() => {
      finishReducedMotionAnimations(modal);
      requestAnimationFrame(() => finishReducedMotionAnimations(modal));
    });
  };

  const remapQuickBuyIds = (modal) => {
    const content = modal.querySelector("[data-guru-quick-buy-content]");
    if (!content || content.dataset.guruQuickBuyIdsRemapped === modal.id) return;

    const instance = String(modal.dataset.guruQuickBuyInstance || modal.id)
      .replace(/[^a-zA-Z0-9_-]/g, "-");
    const idMap = new Map();
    content.querySelectorAll("[id]").forEach((element, index) => {
      const previous = element.id;
      const next = `guru-quick-buy-${instance}-${index + 1}`;
      idMap.set(previous, next);
      element.id = next;
    });
    ["for", "aria-controls", "aria-labelledby", "aria-describedby", "form"].forEach((attribute) => {
      content.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const values = element.getAttribute(attribute)?.split(/\s+/) || [];
        element.setAttribute(
          attribute,
          values.map((value) => idMap.get(value) || value).join(" "),
        );
      });
    });
    content.querySelectorAll('input[type="radio"][name]').forEach((input) => {
      input.name = `${input.name}-${instance}`;
    });
    content.dataset.guruQuickBuyIdsRemapped = modal.id;
  };

  const focusFirstQuickBuyControl = (modal) => {
    const activeElement = document.activeElement;
    if (
      !window.matchMedia(COARSE_POINTER).matches
      && (activeElement === modal || modal.contains(activeElement))
    ) return;
    const control = modal.querySelector(
      "[data-guru-option-select]:not(:disabled), [data-guru-option-value]:not(:disabled), [data-guru-quick-buy-submit]:not(:disabled)",
    );
    if (isVisible(control)) control.focus({ preventScroll: true });
  };

  const searchOriginFor = (trigger) => trigger.closest(
    '[data-guru-panel="search"], [data-component-overlay="search"]',
  );

  const releaseQuickBuyOperation = (modal, operation) => {
    if (quickBuyOperations.get(modal) === operation) quickBuyOperations.delete(modal);
    if (
      operation.searchOrigin
      && predictiveQuickBuyOperations.get(operation.searchOrigin) === operation
    ) predictiveQuickBuyOperations.delete(operation.searchOrigin);
  };

  const visibleSearchReturnTarget = (searchOrigin) => {
    if (!searchOrigin) return null;
    const panelId = searchOrigin.id;
    const selectors = [
      '[data-guru-open="search"]',
      panelId ? `[aria-controls="${escapeSelector(panelId)}"]` : "",
      "[data-guru-cart-trigger]",
      "header a[href]",
      "header button:not(:disabled)",
    ].filter(Boolean).join(", ");
    return Array.from(document.querySelectorAll(selectors)).find((candidate) => (
      !searchOrigin.contains(candidate) && isVisible(candidate)
    )) || null;
  };

  const visiblePageReturnTarget = (excludedRoot) => Array.from(document.querySelectorAll(
    '[data-guru-cart-trigger], header a[href], header button:not(:disabled), main a[href], main button:not(:disabled)',
  )).find((candidate) => (
    !excludedRoot?.contains(candidate) && isVisible(candidate)
  )) || null;

  const closeSearchWithoutFocus = () => {
    if (window.GuruGlobalNavigation?.close) {
      window.GuruGlobalNavigation.close({
        restoreFocus: false,
        immediate: true,
        preservePredictiveQuickBuy: true,
      });
    } else {
      document.dispatchEvent(new CustomEvent("guru:overlay:close", {
        detail: {
          restoreFocus: false,
          immediate: true,
          preservePredictiveQuickBuy: true,
        },
      }));
    }
  };

  const restoreSearchAndFocus = (trigger) => {
    if (!trigger?.isConnected) return;
    if (window.GuruGlobalNavigation?.open) {
      window.GuruGlobalNavigation.open("search", trigger);
    } else {
      document.dispatchEvent(new CustomEvent("guru:overlay:open", {
        detail: { name: "search", trigger },
      }));
    }
    if (isVisible(trigger)) trigger.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      if (isVisible(trigger)) trigger.focus({ preventScroll: true });
    });
  };

  const updateExpandedWidth = (action) => {
    const label = action.querySelector(".guru-product-card__quick-add-label");
    const figure = action.closest(".guru-product-card__figure");
    if (!label || !figure) return;
    const naturalWidth = Math.ceil(label.scrollWidth + 68);
    const availableWidth = Math.max(124, Math.floor(figure.getBoundingClientRect().width - 24));
    action.style.setProperty(
      "--guru-quick-add-expanded-width",
      `${Math.min(Math.max(124, naturalWidth), availableWidth)}px`,
    );
  };

  const updateExpandedWidths = (scope = document) => {
    let updated = 0;
    scope.querySelectorAll?.(".guru-product-card__quick-add").forEach((action) => {
      updateExpandedWidth(action);
      updated += 1;
    });
    return updated;
  };

  const openQuickBuy = async (trigger) => {
    const modalId = trigger.getAttribute("aria-controls");
    const modal = modalId ? document.getElementById(modalId) : null;
    const card = trigger.closest(CARD_SELECTOR);
    if (!modal?.matches(MODAL_SELECTOR) || !card || trigger.getAttribute("aria-busy") === "true") return;

    const searchOrigin = searchOriginFor(trigger);
    const sourceSection = trigger.closest(".shopify-section");
    predictiveQuickBuyOperations.get(searchOrigin)?.cancel();
    const operation = {
      id: ++quickBuyOperationSequence,
      cancelled: false,
      trigger,
      card,
      searchOrigin,
      returnTarget: searchOrigin ? visibleSearchReturnTarget(searchOrigin) : trigger,
      sourceSection,
    };
    let added = false;
    let didShow = false;
    setCardError(card);
    setModalControlsState(modal, { expanded: false, busy: true });
    modal.dataset.guruQuickBuySuccess = "false";

    const onVariantAdded = () => {
      added = true;
      modal.dataset.guruQuickBuySuccess = "true";
    };
    const onBeforeShow = () => {
      if (!isCurrent()) {
        modal.cancelPendingShow?.();
        return;
      }
      if (searchOrigin) closeSearchWithoutFocus();
      remapQuickBuyIds(modal);
      setModalControlsState(modal, { expanded: true });
      settleReducedMotionAnimations(modal);
    };
    const onAfterShow = () => {
      if (!isCurrent()) return;
      setModalControlsState(modal, { expanded: true });
      settleReducedMotionAnimations(modal);
      requestAnimationFrame(() => focusFirstQuickBuyControl(modal));
    };
    const onBeforeHide = () => settleReducedMotionAnimations(modal);
    const cleanup = () => {
      modal.removeEventListener("variant:add", onVariantAdded);
      modal.removeEventListener("dialog:before-show", onBeforeShow);
      modal.removeEventListener("dialog:after-show", onAfterShow);
      modal.removeEventListener("dialog:before-hide", onBeforeHide);
      modal.removeEventListener("dialog:after-hide", onAfterHide);
      document.removeEventListener("keydown", onPendingKeyDown, true);
      document.removeEventListener("guru:overlay:change", onOverlayChange);
    };
    const onAfterHide = () => {
      setModalControlsState(modal, { expanded: false, busy: false });
      cleanup();
      releaseQuickBuyOperation(modal, operation);
      if (
        !operation.cancelled
        && searchOrigin
        && (!added || cartMode() === "message")
      ) restoreSearchAndFocus(trigger);
      delete modal.dataset.guruQuickBuySuccess;
    };
    operation.cleanup = cleanup;
    operation.cancel = ({ restoreSearch = false, focusSearchInput = false } = {}) => {
      if (operation.cancelled) return;
      operation.cancelled = true;
      cleanup();
      releaseQuickBuyOperation(modal, operation);
      modal.cancelPendingShow?.();
      setModalControlsState(modal, { expanded: false, busy: false });
      delete modal.dataset.guruQuickBuySuccess;
      if (restoreSearch && searchOrigin && trigger.isConnected) {
        restoreSearchAndFocus(trigger);
      } else if (focusSearchInput && searchOrigin && !searchOrigin.hidden) {
        searchOrigin.querySelector("[data-guru-search-input]")?.focus({ preventScroll: true });
      }
    };
    quickBuyOperations.set(modal, operation);
    if (searchOrigin) predictiveQuickBuyOperations.set(searchOrigin, operation);

    const isCurrent = () => (
      !operation.cancelled
      && quickBuyOperations.get(modal) === operation
      && (!searchOrigin || predictiveQuickBuyOperations.get(searchOrigin) === operation)
      && modal.isConnected
      && trigger.isConnected
      && (!sourceSection || sourceSection.isConnected)
    );
    const assertCurrent = () => {
      if (!isCurrent()) throw new DOMException("Quick Buy was cancelled", "AbortError");
    };
    const onPendingKeyDown = (event) => {
      if (event.key !== "Escape" || modal.open || !searchOrigin || !isCurrent()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      operation.cancel({ restoreSearch: true });
    };
    const onOverlayChange = (event) => {
      if (
        event.detail?.name !== "search"
        || event.detail?.open !== false
        || modal.open
        || !searchOrigin
        || !isCurrent()
      ) return;
      const eventRoot = event.detail?.root;
      if (eventRoot && !eventRoot.contains(searchOrigin)) return;
      operation.cancel();
    };

    modal.addEventListener("variant:add", onVariantAdded);
    modal.addEventListener("dialog:before-show", onBeforeShow);
    modal.addEventListener("dialog:after-show", onAfterShow);
    modal.addEventListener("dialog:before-hide", onBeforeHide);
    modal.addEventListener("dialog:after-hide", onAfterHide);
    document.addEventListener("keydown", onPendingKeyDown, true);
    document.addEventListener("guru:overlay:change", onOverlayChange);

    try {
      await customElements.whenDefined("quick-buy-modal");
      assertCurrent();
      await modal.show();
      assertCurrent();
      didShow = true;
    } catch (error) {
      const wasCurrent = isCurrent();
      const shouldReport = wasCurrent && error?.name !== "AbortError";
      cleanup();
      releaseQuickBuyOperation(modal, operation);
      if (wasCurrent) setModalControlsState(modal, { expanded: false, busy: false });
      if (shouldReport) {
        setCardError(card, translate("quickBuyLoadError"));
        if (searchOrigin) restoreSearchAndFocus(trigger);
        else if (trigger.isConnected) trigger.focus({ preventScroll: true });
      }
    } finally {
      if (isCurrent()) setModalControlsState(modal, { busy: false });
      if (!didShow && quickBuyOperations.get(modal) !== operation) {
        delete modal.dataset.guruQuickBuySuccess;
      }
    }
  };

  const cartUrl = (path) => `${window.Shopify?.routes?.root || "/"}${path}`;

  const readCart = async () => {
    const response = await fetch(cartUrl("cart.js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const cart = await response.json();
    if (!cart || !Array.isArray(cart.items)) throw new Error("Invalid cart response");
    return cart;
  };

  const canonicalProperties = (entries) => {
    const properties = [];
    const keys = new Set();
    for (const [rawKey, rawValue] of entries) {
      const key = String(rawKey || "");
      const value = String(rawValue ?? "");
      if (!key || !value) continue;
      if (keys.has(key)) return null;
      keys.add(key);
      properties.push([key, value]);
    }
    properties.sort(([left], [right]) => left.localeCompare(right));
    return JSON.stringify(properties);
  };

  const requestedCommerceIdentity = (formData, variantId) => {
    const sellingPlan = String(formData.get("selling_plan") || "").trim();
    if (sellingPlan) return null;
    const properties = canonicalProperties(
      [...formData.entries()]
        .map(([name, value]) => {
          const match = name.match(/^properties\[(.+)\]$/);
          return match ? [match[1], value] : null;
        })
        .filter(Boolean),
    );
    return properties === null || properties.length > MAX_COMMERCE_IDENTITY_LENGTH
      ? null
      : { variantId, properties };
  };

  const cartLineProperties = (item) => canonicalProperties(
    item?.properties && typeof item.properties === "object" && !Array.isArray(item.properties)
      ? Object.entries(item.properties)
      : [],
  );

  const commerceQuantity = (cart, identity) => (
    (cart?.items || []).reduce((total, item) => {
      const isOneTime = !item?.selling_plan_allocation && !item?.selling_plan;
      const matches = isOneTime
        && Number(item.variant_id || item.id) === identity.variantId
        && cartLineProperties(item) === identity.properties;
      return matches ? total + Number(item.quantity || 0) : total;
    }, 0)
  );

  const cartScopeToken = (cart) => {
    const token = typeof cart?.token === "string" ? cart.token.trim() : "";
    return token && token.length <= 512 && !/\s/.test(token) ? token : null;
  };

  const commerceOperationKey = (cart, identity) => {
    const cartToken = cartScopeToken(cart);
    return cartToken
      ? JSON.stringify([cartToken, identity.variantId, identity.properties])
      : null;
  };

  const releaseCommerceOperation = (state) => {
    if (state?.key && commerceOperationLedger.get(state.key) === state) {
      commerceOperationLedger.delete(state.key);
    }
  };

  const isCurrentCommerceOperation = (state) => Boolean(
    state?.key && commerceOperationLedger.get(state.key) === state,
  );

  const operationOwner = (form) => form.closest(CARD_SELECTOR) || form;

  const commerceSubmissionKey = (identity) => JSON.stringify([
    identity.variantId,
    identity.properties,
  ]);

  const cartAddTimeoutFor = (form) => {
    const requested = Number(
      form.dataset.guruCartAddTimeout
      || form.closest(CARD_SELECTOR)?.dataset.guruCartAddTimeout,
    );
    return Number.isFinite(requested)
      ? Math.min(CART_ADD_TIMEOUT_MAX, Math.max(CART_ADD_TIMEOUT_MIN, requested))
      : CART_ADD_TIMEOUT;
  };

  const cartErrorBodyTimeoutFor = (form) => {
    const requested = Number(
      form.dataset.guruCartErrorBodyTimeout
      || form.closest(CARD_SELECTOR)?.dataset.guruCartErrorBodyTimeout,
    );
    return Number.isFinite(requested)
      ? Math.min(
        CART_ERROR_BODY_TIMEOUT_MAX,
        Math.max(CART_ERROR_BODY_TIMEOUT_MIN, Math.floor(requested)),
      )
      : CART_ERROR_BODY_TIMEOUT;
  };

  const setFormBusy = (form, busy) => {
    const owner = operationOwner(form);
    const submitButtons = owner === form
      ? Array.from(form.elements).filter(
        (element) => element instanceof HTMLButtonElement && element.type === "submit",
      )
      : Array.from(owner.querySelectorAll(`${FORM_SELECTOR} button[type="submit"]`));
    submitButtons.forEach((button) => {
      if (busy) button.setAttribute("aria-busy", "true");
      else button.removeAttribute("aria-busy");
    });
  };

  const cartMode = () => {
    const settings = window.themeVariables?.settings || {};
    if (settings.pageType === "cart" || settings.cartType === "page") return "page";
    if (settings.cartType === "message") return "message";
    return "drawer";
  };

  const showFormSuccess = (form) => {
    const message = form.closest(MODAL_SELECTOR)?.querySelector("[data-guru-quick-buy-error]")
      || form.closest(CARD_SELECTOR)?.querySelector("[data-guru-product-card-error]");
    if (!message) return;
    message.classList.add("is-success");
    message.setAttribute("role", "status");
    message.setAttribute("aria-live", "polite");
    message.textContent = translate("addedToCartAnnouncement");
    message.hidden = false;
    window.setTimeout(() => {
      if (!message.isConnected || !message.classList.contains("is-success")) return;
      message.classList.remove("is-success");
      message.setAttribute("role", "alert");
      message.removeAttribute("aria-live");
      message.textContent = "";
      message.hidden = true;
    }, 2500);
  };

  const dispatchCartError = (form, message) => {
    form.dispatchEvent(new CustomEvent("cart:error", {
      bubbles: true,
      detail: { error: message },
    }));
  };

  const dispatchCartSuccess = (form, state, cart) => {
    if (state.dispatched) return;
    state.dispatched = true;
    cart.sections = state.sections || {};
    const modal = form.closest(MODAL_SELECTOR);
    const quickBuyOperation = modal ? quickBuyOperations.get(modal) : null;
    const drawerReturnTarget = cartMode() !== "drawer"
      ? null
      : modal
        ? quickBuyOperation?.searchOrigin
          ? visibleSearchReturnTarget(quickBuyOperation.searchOrigin) || quickBuyOperation.returnTarget
          : [
            quickBuyOperation?.trigger,
            quickBuyOperation?.card?.querySelector(".guru-product-card__identity"),
          ].find(isVisible) || visiblePageReturnTarget(modal)
        : state.searchOrigin
          ? [
            state.drawerReturnTarget,
            visibleSearchReturnTarget(state.searchOrigin),
          ].find(isVisible) || visiblePageReturnTarget(state.searchOrigin)
          : isVisible(document.activeElement)
            ? document.activeElement
            : visiblePageReturnTarget(form);
    const detail = {
      items: state.responseJson?.items || (state.responseJson?.id ? [state.responseJson] : [{ id: state.variantId }]),
      cart,
      onSuccessDo: state.onSuccessDo,
    };
    form.dispatchEvent(new CustomEvent("variant:add", { bubbles: true, detail }));
    if (cartMode() === "drawer" && !modal && state.searchOrigin) {
      closeSearchWithoutFocus();
    }
    // Prestige's drawer FocusTrap snapshots the active element when cart:change
    // opens it. Quick Buy removes its focused submit after its leave animation,
    // so hand focus to a visible connected catalogue trigger or search opener
    // before the drawer captures its return target.
    if (isVisible(drawerReturnTarget)) drawerReturnTarget.focus({ preventScroll: true });
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: {
        baseEvent: "variant:add",
        onSuccessDo: state.onSuccessDo,
        cart,
      },
    }));
    if (cartMode() === "message") showFormSuccess(form);
    state.phase = "idle";
    releaseCommerceOperation(state);
  };

  const validBundledSections = (sections, sectionIds) => (
    sections
    && !Array.isArray(sections)
    && typeof sections === "object"
    && sectionIds.every((id) => typeof sections[id] === "string" && sections[id].trim())
  );

  const readBundledSections = async (sectionIds) => {
    if (sectionIds.length === 0) return {};
    const url = new URL(window.Shopify?.routes?.root || "/", window.location.origin);
    url.searchParams.set("sections", sectionIds.join(","));
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const sections = await response.json();
    if (!validBundledSections(sections, sectionIds)) throw new Error("Invalid bundled sections");
    return sections;
  };

  const finishCommittedMutation = async (form, state, knownCart = null) => {
    const cart = knownCart || await readCart();
    const currentQuantity = commerceQuantity(cart, state.identity);
    if (currentQuantity !== state.baselineQuantity + state.quantity) {
      state.phase = "uncertain";
      dispatchCartError(form, translate("cartStateUncertain"));
      return false;
    }
    if (cartMode() === "page") {
      state.dispatched = true;
      state.phase = "idle";
      releaseCommerceOperation(state);
      window.location.assign(cartUrl("cart"));
      return true;
    }
    const responseSections = state.responseJson?.sections;
    state.sections = validBundledSections(responseSections, state.sectionIds)
      ? responseSections
      : await readBundledSections(state.sectionIds);
    dispatchCartSuccess(form, state, cart);
    return true;
  };

  const reconcileDelay = (duration) => new Promise((resolve) => {
    window.setTimeout(resolve, duration);
  });

  const runUncertainReconciliation = async (form, state, initialCart = null) => {
    const expectedQuantity = state.baselineQuantity + state.quantity;
    const delays = [0, 220, 520];

    for (const [index, delay] of delays.entries()) {
      if (delay > 0) await reconcileDelay(delay);
      if (!isCurrentCommerceOperation(state) || state.dispatched) return true;
      try {
        const cart = index === 0 && initialCart ? initialCart : await readCart();
        if (!isCurrentCommerceOperation(state) || state.dispatched) return true;
        if (commerceOperationKey(cart, state.identity) !== state.key) break;
        const currentQuantity = commerceQuantity(cart, state.identity);
        if (currentQuantity === expectedQuantity) {
          state.phase = "committed";
          try {
            return await finishCommittedMutation(form, state, cart);
          } catch (_error) {
            if (isCurrentCommerceOperation(state)) {
              state.phase = "uncertain";
              dispatchCartError(form, translate("cartStateUncertain"));
            }
            return false;
          }
        }
        if (currentQuantity !== state.baselineQuantity) break;
      } catch (_error) {
        // A failed cart read is still an unknown POST outcome. Keep reconciling.
      }
    }

    if (isCurrentCommerceOperation(state)) {
      state.phase = "uncertain";
      dispatchCartError(form, translate("cartStateUncertain"));
    }
    return false;
  };

  const reconcileUncertainMutation = (form, state, initialCart = null) => {
    if (!isCurrentCommerceOperation(state) || state.dispatched) return Promise.resolve(false);
    if (state.reconcilePromise) {
      state.reconcileQueued = true;
      if (form?.isConnected) state.reconcileForm = form;
      if (initialCart) state.reconcileInitialCart = initialCart;
      return state.reconcilePromise;
    }

    state.reconcileForm = form;
    state.reconcileInitialCart = initialCart;
    let reconciliation;
    reconciliation = (async () => {
      let committed = false;
      do {
        state.reconcileQueued = false;
        const reconciliationForm = state.reconcileForm || form;
        const reconciliationCart = state.reconcileInitialCart;
        state.reconcileForm = null;
        state.reconcileInitialCart = null;
        committed = await runUncertainReconciliation(
          reconciliationForm,
          state,
          reconciliationCart,
        );
      } while (
        !committed
        && isCurrentCommerceOperation(state)
        && state.reconcileQueued
      );
      return committed;
    })().catch(() => {
      if (isCurrentCommerceOperation(state)) {
        state.phase = "uncertain";
        dispatchCartError(form, translate("cartStateUncertain"));
      }
      return false;
    }).finally(() => {
      if (state.reconcilePromise === reconciliation) state.reconcilePromise = null;
    });
    state.reconcilePromise = reconciliation;
    return reconciliation;
  };

  const queueUncertainReconciliation = (form, state) => {
    queueMicrotask(() => {
      if (!isCurrentCommerceOperation(state) || state.dispatched) return;
      void reconcileUncertainMutation(form, state);
    });
  };

  const tryResponseJson = async (response) => {
    try {
      return await response.json();
    } catch (_error) {
      return {};
    }
  };

  const normalizeResponseJson = (value) => (
    value && typeof value === "object" && !Array.isArray(value) ? value : {}
  );

  const cartErrorMessage = (responseJson) => {
    const description = typeof responseJson?.description === "string"
      ? responseJson.description.trim()
      : "";
    return description && description.length <= CART_ERROR_DESCRIPTION_MAX
      ? description
      : translate("cartAddError");
  };

  const readBoundedCartErrorBody = async (response, form, controller) => {
    const bodyRequest = tryResponseJson(response).then(normalizeResponseJson);
    let bodyTimedOut = false;
    let resolveBodyDeadline;
    const bodyDeadline = new Promise((resolve) => {
      resolveBodyDeadline = resolve;
    });
    const bodyTimer = window.setTimeout(() => {
      bodyTimedOut = true;
      controller.abort();
      resolveBodyDeadline({});
    }, cartErrorBodyTimeoutFor(form));
    const responseJson = await Promise.race([bodyRequest, bodyDeadline]);
    window.clearTimeout(bodyTimer);
    return bodyTimedOut ? {} : responseJson;
  };

  const submitProductCardForm = async (form) => {
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const card = form.closest(CARD_SELECTOR);
    if (card) setCardError(card);

    const variantId = Number(form.elements.id?.value);
    const quantityInput = form.elements.quantity;
    const quantity = Number(quantityInput?.value);
    const quantityRule = quantityRuleForInput(quantityInput);
    const formData = new FormData(form);
    const identity = requestedCommerceIdentity(formData, variantId);
    const formSearchOrigin = searchOriginFor(form);
    const formDrawerReturnTarget = visibleSearchReturnTarget(formSearchOrigin);
    if (
      !Number.isSafeInteger(variantId)
      || variantId <= 0
      || !quantityAllowed(quantity, quantityRule)
      || !identity
    ) {
      dispatchCartError(form, translate("cartAddError"));
      return;
    }

    const submissionKey = commerceSubmissionKey(identity);
    const activeSubmission = commerceSubmissionLocks.get(submissionKey);
    if (activeSubmission) {
      activeSubmission.forms.add(form);
      setFormBusy(form, true);
      return;
    }
    const submission = { forms: new Set([form]) };
    commerceSubmissionLocks.set(submissionKey, submission);
    setFormBusy(form, true);
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      let baselineCart;
      try {
        baselineCart = await readCart();
      } catch (_error) {
        dispatchCartError(form, translate("cartStateUncertain"));
        return;
      }

      const key = commerceOperationKey(baselineCart, identity);
      if (!key) {
        dispatchCartError(form, translate("cartStateUncertain"));
        return;
      }

      const previous = commerceOperationLedger.get(key);
      if (previous && formSearchOrigin) {
        previous.searchOrigin = formSearchOrigin;
        previous.drawerReturnTarget = formDrawerReturnTarget;
      }
      if (["preparing", "adding"].includes(previous?.phase)) return;
      if (previous?.reconcilePromise) {
        await reconcileUncertainMutation(form, previous, baselineCart);
        return;
      }
      if (previous?.phase === "committed") {
        try {
          await finishCommittedMutation(form, previous, baselineCart);
        } catch (_error) {
          dispatchCartError(form, translate("cartStateUncertain"));
        }
        return;
      }
      if (previous?.phase === "uncertain") {
        await reconcileUncertainMutation(form, previous, baselineCart);
        return;
      }
      if (previous) releaseCommerceOperation(previous);
      if (commerceOperationLedger.size >= MAX_COMMERCE_OPERATIONS) {
        dispatchCartError(form, translate("cartStateUncertain"));
        return;
      }

      const state = {
        phase: "adding",
        key,
        variantId,
        identity,
        quantity,
        baselineQuantity: commerceQuantity(baselineCart, identity),
        onSuccessDo: "",
        responseJson: null,
        sectionIds: [],
        sections: null,
        dispatched: false,
        searchOrigin: formSearchOrigin,
        drawerReturnTarget: formDrawerReturnTarget,
        reconcilePromise: null,
        reconcileQueued: false,
        reconcileForm: null,
        reconcileInitialCart: null,
      };
      commerceOperationLedger.set(key, state);

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections },
      }));
      state.sectionIds = [...new Set(sections.filter((id) => typeof id === "string" && id.trim()))];
      formData.set("sections", state.sectionIds.join(","));
      formData.set("sections_url", cartUrl(`variants/${variantId}`));

      const addController = new AbortController();
      let addDeadlineExpired = false;
      let rejectAddDeadline;
      const addDeadline = new Promise((_resolve, reject) => {
        rejectAddDeadline = reject;
      });
      const addTimer = window.setTimeout(() => {
        addDeadlineExpired = true;
        addController.abort();
        rejectAddDeadline(new DOMException("Cart add request timed out", "TimeoutError"));
      }, cartAddTimeoutFor(form));
      const clearAddTimer = () => window.clearTimeout(addTimer);
      const observeUnknownLateSettlement = (request) => request.then(
        () => {
          if (addDeadlineExpired) queueUncertainReconciliation(form, state);
        },
        () => {
          if (addDeadlineExpired) queueUncertainReconciliation(form, state);
        },
      );
      const addResponseRequest = (async () => fetch(cartUrl("cart/add.js"), {
        body: formData,
        credentials: "same-origin",
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        signal: addController.signal,
      }))();
      observeUnknownLateSettlement(addResponseRequest);
      try {
        const response = await Promise.race([addResponseRequest, addDeadline]);
        if (
          !response
          || typeof response.ok !== "boolean"
          || !Number.isInteger(response.status)
        ) {
          clearAddTimer();
          throw new Error("Invalid cart add response");
        }

        if (response.status === 422) {
          clearAddTimer();
          state.phase = "idle";
          releaseCommerceOperation(state);
          const responseJson = await readBoundedCartErrorBody(response, form, addController);
          dispatchCartError(form, cartErrorMessage(responseJson));
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

        if (!response.ok) {
          clearAddTimer();
          state.phase = "uncertain";
          await reconcileUncertainMutation(form, state);
          return;
        }

        const addBodyRequest = tryResponseJson(response).then(normalizeResponseJson);
        observeUnknownLateSettlement(addBodyRequest);
        state.responseJson = await Promise.race([addBodyRequest, addDeadline]);
      } catch (error) {
        state.phase = "uncertain";
        if (addDeadlineExpired || error?.name === "TimeoutError") {
          dispatchCartError(form, translate("cartStateUncertain"));
          queueUncertainReconciliation(form, state);
          return;
        }
        await reconcileUncertainMutation(form, state);
        return;
      } finally {
        clearAddTimer();
      }

      state.phase = "uncertain";
      await reconcileUncertainMutation(form, state);
    } finally {
      if (commerceSubmissionLocks.get(submissionKey) === submission) {
        commerceSubmissionLocks.delete(submissionKey);
      }
      submission.forms.forEach((submissionForm) => setFormBusy(submissionForm, false));
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  };

  class GuruQuickBuyPicker extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const { signal } = this.listeners;

      this.form = this.closest("form");
      this.modal = this.closest(MODAL_SELECTOR);
      this.variantInput = this.querySelector("[data-guru-quick-buy-variant-id]");
      this.error = this.querySelector("[data-guru-quick-buy-error]");
      this.announcement = this.querySelector("[data-guru-quick-buy-announcement]");
      this.groups = [...this.querySelectorAll("[data-guru-option-group]")]
        .sort((left, right) => Number(left.dataset.optionIndex) - Number(right.dataset.optionIndex));

      try {
        const variants = JSON.parse(
          this.querySelector("[data-guru-quick-buy-variants]")?.textContent || "[]",
        );
        this.variants = Array.isArray(variants)
          ? variants.map((variant) => {
            const quantityRule = normalizeQuantityRule(variant?.quantityRule);
            const priceAmount = Number(variant?.priceAmount);
            const linePriceAmount = Number(variant?.linePriceAmount);
            const quantityPriceBreaks = normalizeQuantityPriceBreaks(
              variant?.quantityPriceBreaks,
              quantityRule,
              priceAmount,
              linePriceAmount,
            );
            if (
              !positiveSafeInteger(variant?.id)
              || !Array.isArray(variant?.options)
              || !variant.options.every((option) => typeof option === "string")
              || variant.options.length !== this.groups.length
              || typeof variant?.available !== "boolean"
              || typeof variant?.requiresSellingPlan !== "boolean"
              || typeof variant?.price !== "string"
              || !variant.price.trim()
              || typeof variant?.linePrice !== "string"
              || !variant.linePrice.trim()
              || !Number.isSafeInteger(priceAmount)
              || priceAmount < 0
              || !Number.isSafeInteger(linePriceAmount)
              || linePriceAmount < 0
              || !quantityRule
              || !quantityPriceBreaks
            ) return null;
            return {
              ...variant,
              priceAmount,
              linePriceAmount,
              quantityRule,
              quantityPriceBreaks,
            };
          })
          : [];
        const variantIds = new Set(this.variants.map((variant) => variant?.id));
        const combinations = new Set(this.variants.map((variant) => variant?.options.join("\u0000")));
        if (
          this.variants.length === 0
          || this.variants.some((variant) => variant === null)
          || variantIds.size !== this.variants.length
          || combinations.size !== this.variants.length
        ) this.variants = [];
      } catch (_error) {
        this.variants = [];
      }

      this.addEventListener("click", (event) => this.onOptionButton(event), { signal });
      this.addEventListener("change", (event) => this.onOptionSelect(event), { signal });
      this.addEventListener("pointerdown", (event) => this.onPointerDown(event), { capture: true, signal });
      this.addEventListener("keydown", () => this.clearPointerFocus(), { capture: true, signal });
      this.form?.addEventListener("cart:error", (event) => this.onCartError(event), { signal });
      this.form?.addEventListener("variant:add", () => this.onVariantAdded(), { signal });

      const initialId = String(this.variantInput?.value || "");
      const initialVariant = this.variants.find((variant) => (
        String(variant.id) === initialId && this.isEligible(variant)
      )) || this.variants.find((variant) => this.isEligible(variant));

      if (initialVariant) this.applyVariant(initialVariant, { announce: false });
      else this.failClosed(translate("noVariantData"));
    }

    disconnectedCallback() {
      this.listeners?.abort();
    }

    selectedValues() {
      return this.groups.map((group) => {
        const select = group.querySelector("[data-guru-option-select]");
        if (select) return select.value;
        return group.querySelector('[data-guru-option-value][aria-pressed="true"]')?.value || "";
      });
    }

    variantForSelection(selection) {
      return this.variants.find((variant) => (
        variant.options.length === selection.length
        && variant.options.every((value, index) => value === selection[index])
      ));
    }

    isEligible(variant) {
      return Boolean(variant?.available && variant.requiresSellingPlan === false);
    }

    availableVariantForPrefix(selection, lastIndex) {
      return this.variants.find((variant) => (
        this.isEligible(variant)
        && selection.slice(0, lastIndex + 1)
          .every((value, index) => variant.options[index] === value)
      ));
    }

    onOptionButton(event) {
      const button = event.target.closest("[data-guru-option-value]");
      if (!button || button.disabled || !this.contains(button)) return;
      const group = button.closest("[data-guru-option-group]");
      const index = Number(group?.dataset.optionIndex);
      const selection = this.selectedValues();
      selection[index] = button.value;
      this.chooseSelection(selection, index);
    }

    onOptionSelect(event) {
      const select = event.target.closest("[data-guru-option-select]");
      if (!select || !this.contains(select)) return;
      const group = select.closest("[data-guru-option-group]");
      const index = Number(group?.dataset.optionIndex);
      const selection = this.selectedValues();
      selection[index] = select.value;
      this.chooseSelection(selection, index);
      if (select.hasAttribute("data-pointer-focus")) select.blur();
    }

    chooseSelection(selection, changedIndex) {
      const exactVariant = this.variantForSelection(selection);
      const nextVariant = this.isEligible(exactVariant)
        ? exactVariant
        : this.availableVariantForPrefix(selection, changedIndex)
          || this.variants.find((variant) => (
            this.isEligible(variant) && variant.options[changedIndex] === selection[changedIndex]
          ))
          || exactVariant;
      if (nextVariant) this.applyVariant(nextVariant);
      else this.showError(translate("combinationUnavailable"));
    }

    syncControls(variant) {
      const selection = variant.options;
      this.groups.forEach((group, index) => {
        const prefix = selection.slice(0, index);
        const isValueAvailable = (value) => this.variants.some((candidate) => (
          this.isEligible(candidate)
          && candidate.options[index] === value
          && prefix.every((prefixValue, prefixIndex) => candidate.options[prefixIndex] === prefixValue)
        ));
        const select = group.querySelector("[data-guru-option-select]");
        if (select) {
          [...select.options].forEach((option) => { option.disabled = !isValueAvailable(option.value); });
          select.value = selection[index];
          return;
        }
        group.querySelectorAll("[data-guru-option-value]").forEach((button) => {
          const selected = button.value === selection[index];
          button.disabled = !isValueAvailable(button.value);
          button.classList.toggle("is-selected", selected);
          button.setAttribute("aria-pressed", String(selected));
        });
      });
    }

    applyVariant(variant, { announce = true } = {}) {
      this.currentVariant = variant;
      this.syncControls(variant);
      if (this.variantInput) {
        this.variantInput.disabled = false;
        this.variantInput.value = String(variant.id);
      }
      const quantityInput = this.form?.elements.quantity;
      if (quantityInput) {
        quantityInput.disabled = false;
        quantityInput.value = String(variant.quantityRule.minimum);
        quantityInput.dataset.guruQuantityMin = String(variant.quantityRule.minimum);
        quantityInput.dataset.guruQuantityIncrement = String(variant.quantityRule.increment);
        if (variant.quantityRule.maximum === null) delete quantityInput.dataset.guruQuantityMax;
        else quantityInput.dataset.guruQuantityMax = String(variant.quantityRule.maximum);
      }

      const title = this.modal?.querySelector(".guru-quick-buy__identity > strong")?.textContent?.trim()
        || translate("productFallback");
      const variantLabel = this.modal?.querySelector("[data-guru-quick-buy-variant]");
      if (variantLabel) {
        const named = variant.title && variant.title !== "Default Title";
        variantLabel.textContent = named ? variant.title : "";
        variantLabel.hidden = !named;
      }

      const image = this.modal?.querySelector("[data-guru-quick-buy-image]");
      if (image && variant.image) {
        image.src = variant.image;
        image.removeAttribute("srcset");
        image.alt = "";
        image.closest("[data-guru-card-media-source]")?.setAttribute(
          "data-guru-card-media-source",
          variant.imageSource === "verified-card-image"
            ? "verified-card-image"
            : "shopify-featured-unverified",
        );
      }

      const submit = this.querySelector("[data-guru-quick-buy-submit]");
      if (submit) {
        submit.disabled = !this.isEligible(variant);
        submit.textContent = this.isEligible(variant)
          ? `${translate("addToCart")} · ${variant.linePrice}`
          : variant.requiresSellingPlan
            ? translate("viewProduct")
            : translate("soldOut");
      }

      const summaryPrice = this.modal?.querySelector("[data-guru-quick-buy-price]");
      if (summaryPrice) summaryPrice.textContent = variant.price;
      const summaryCompare = this.modal?.querySelector("[data-guru-quick-buy-compare]");
      if (summaryCompare) {
        summaryCompare.textContent = variant.compareAtPrice || "";
        summaryCompare.hidden = !variant.compareAtPrice;
      }

      this.showError();
      if (announce && this.announcement) {
        this.announcement.textContent = variant.title && variant.title !== "Default Title"
          ? translate("variantSelected", { variant: variant.title, price: variant.linePrice })
          : translate("productSelected", { product: title, price: variant.linePrice });
      }
    }

    failClosed(message) {
      this.currentVariant = null;
      if (this.variantInput) {
        this.variantInput.value = "";
        this.variantInput.disabled = true;
      }
      const quantityInput = this.form?.elements.quantity;
      if (quantityInput) quantityInput.disabled = true;
      this.groups.forEach((group) => {
        group.querySelectorAll("button, select").forEach((control) => {
          control.disabled = true;
        });
      });
      const submit = this.querySelector("[data-guru-quick-buy-submit]");
      if (submit) {
        submit.disabled = true;
        submit.removeAttribute("aria-busy");
      }
      this.showError(message);
      if (this.announcement) this.announcement.textContent = message;
    }

    showError(message = "") {
      if (!this.error) return;
      this.error.classList.remove("is-success");
      this.error.setAttribute("role", "alert");
      this.error.removeAttribute("aria-live");
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    onCartError(event) {
      this.showError(event.detail?.error || translate("cartAddError"));
      this.querySelector("[data-guru-quick-buy-submit]")?.focus({ preventScroll: true });
    }

    onVariantAdded() {
      this.showError();
      if (this.modal) this.modal.dataset.guruQuickBuySuccess = "true";
      if (this.announcement) this.announcement.textContent = translate("addedToCartAnnouncement");
    }

    onPointerDown(event) {
      this.clearPointerFocus();
      event.target.closest("[data-guru-option-select]")?.setAttribute("data-pointer-focus", "");
    }

    clearPointerFocus() {
      this.querySelectorAll("[data-guru-option-select][data-pointer-focus]")
        .forEach((select) => select.removeAttribute("data-pointer-focus"));
    }
  }

  if (!customElements.get("guru-quick-buy-picker")) {
    customElements.define("guru-quick-buy-picker", GuruQuickBuyPicker);
  }

  const safeInteger = positiveSafeInteger;

  const safeProductUrl = (value, expectedHandle) => {
    try {
      const url = new URL(value, window.location.href);
      const parts = url.pathname.split("/").filter(Boolean);
      const productsIndex = parts.lastIndexOf("products");
      if (
        url.origin !== window.location.origin
        || productsIndex < 0
        || productsIndex !== parts.length - 2
      ) return null;
      const urlHandle = decodeURIComponent(parts[productsIndex + 1]);
      if (urlHandle !== expectedHandle) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return null;
    }
  };

  const safeMedia = (value) => {
    if (!value || typeof value.url !== "string") return null;
    try {
      const url = new URL(value.url, window.location.href);
      if (!["http:", "https:"].includes(url.protocol)) return null;
      const isShopifyMedia = url.hostname === "cdn.shopify.com"
        || (url.origin === window.location.origin && url.pathname.includes("/cdn/shop/"));
      if (!isShopifyMedia) return null;
      const width = Number(value.width);
      const height = Number(value.height);
      const opticalScale = value.optical_scale === null || value.optical_scale === undefined
        ? null
        : Number(value.optical_scale);
      if (opticalScale !== null && (!Number.isFinite(opticalScale) || opticalScale < 0.65 || opticalScale > 1)) {
        return null;
      }
      return {
        url: url.href,
        width: Number.isFinite(width) && width > 0 ? width : null,
        height: Number.isFinite(height) && height > 0 ? height : null,
        opticalScale,
      };
    } catch (_error) {
      return null;
    }
  };

  const normalizeViewModel = (input, context = {}) => {
    const productId = safeInteger(input?.id);
    const variantId = safeInteger(input?.selected_variant?.id);
    const handle = typeof input?.handle === "string" ? input.handle : "";
    const productUrl = safeProductUrl(input?.url, handle);
    const price = Number(input?.price?.amount);
    const currency = String(input?.price?.currency_code || "").toUpperCase();
    const expectedCurrency = String(context.currency || config.currency || "").toUpperCase();
    const variantsCount = Number(input?.variants_count);
    const quantityRule = normalizeQuantityRule(input?.selected_variant?.quantity_rule);
    const oneTimeAvailable = input?.one_time_available;
    const selectedRequiresSellingPlan = input?.selected_variant?.requires_selling_plan;
    if (
      !productId || !variantId || !productUrl
      || !/^[a-z0-9][a-z0-9-]{0,127}$/.test(handle)
      || typeof input?.title !== "string" || !input.title.trim()
      || typeof input?.vendor !== "string"
      || typeof input?.available !== "boolean"
      || typeof input?.requires_selling_plan !== "boolean"
      || typeof oneTimeAvailable !== "boolean"
      || typeof input?.selected_variant?.available !== "boolean"
      || typeof selectedRequiresSellingPlan !== "boolean"
      || typeof input?.price_varies !== "boolean"
      || !["from_price", "max_price"].includes(input?.price_strategy)
      || !Number.isSafeInteger(price) || price < 0
      || !/^[A-Z]{3}$/.test(currency) || (expectedCurrency && currency !== expectedCurrency)
      || !Number.isSafeInteger(variantsCount) || variantsCount < 1
      || input.available !== input.selected_variant.available
      || (oneTimeAvailable && (!input.selected_variant.available || selectedRequiresSellingPlan))
      || (input.requires_selling_plan && oneTimeAvailable)
    ) return null;

    let compareAt = null;
    if (input.compare_at_price !== null && input.compare_at_price !== undefined) {
      const amount = Number(input.compare_at_price.amount);
      const compareCurrency = String(input.compare_at_price.currency_code || "").toUpperCase();
      if (!Number.isSafeInteger(amount) || amount <= price || compareCurrency !== currency) return null;
      compareAt = amount;
    }
    const media = input.featured_media === null ? null : safeMedia(input.featured_media);
    if (input.featured_media !== null && !media) return null;

    return {
      productId,
      variantId,
      productUrl,
      handle,
      title: input.title.trim(),
      vendor: input.vendor.trim(),
      available: input.available,
      selectedAvailable: input.selected_variant.available,
      requiresSellingPlan: input.requires_selling_plan,
      oneTimeAvailable,
      selectedRequiresSellingPlan,
      variantsCount,
      price,
      compareAt,
      currency,
      priceVaries: input.price_varies,
      priceStrategy: input.price_strategy,
      quantityRule,
      media,
      position: safeInteger(input.position) || safeInteger(context.position) || 1,
    };
  };

  const formatWithDelimiters = (amount, precision, thousands, decimal) => {
    const fixed = (amount / 100).toFixed(precision);
    const [integer, fraction] = fixed.split(".");
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction === undefined ? grouped : `${grouped}${decimal}${fraction}`;
  };

  const moneyFormatForTheme = () => {
    const settings = window.themeVariables?.settings || {};
    const currencyCodeEnabled = settings.currencyCodeEnabled === true;
    const format = currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat
      : settings.moneyFormat;
    return typeof format === "string" && /\{\{\s*amount/.test(format) ? format : null;
  };

  const formatMoney = (amount) => {
    const format = moneyFormatForTheme();
    if (!format) throw new TypeError("Missing Shopify money format");
    const placeholder = format.match(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/)?.[1];
    const options = {
      amount: [2, ",", "."],
      amount_no_decimals: [0, ",", "."],
      amount_with_comma_separator: [2, ".", ","],
      amount_no_decimals_with_comma_separator: [0, ".", ","],
      amount_with_apostrophe_separator: [2, "'", "."],
      amount_with_space_separator: [2, " ", ","],
      amount_no_decimals_with_space_separator: [0, " ", ","],
      amount_with_period_and_space_separator: [2, " ", "."],
      amount_no_decimals_with_period_and_space_separator: [0, " ", "."],
    };
    if (!placeholder || !options[placeholder]) throw new TypeError("Unsupported Shopify money format");
    const [precision, thousands, decimal] = options[placeholder];
    const formatted = format.replace(
      /\{\{\s*amount(?:_[a-z_]+)?\s*\}\}/,
      formatWithDelimiters(amount, precision, thousands, decimal),
    );
    return new DOMParser().parseFromString(formatted, "text/html").body.textContent || "";
  };

  const create = (tag, className = "") => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    return element;
  };

  const labelledMoney = (tag, className, label, value) => {
    const element = create(tag, className);
    const accessibleLabel = create("span", "sr-only");
    accessibleLabel.textContent = label;
    element.append(accessibleLabel, document.createTextNode(value));
    return element;
  };

  const hiddenInput = (name, value) => {
    const input = create("input");
    input.type = "hidden";
    input.name = name;
    input.value = String(value);
    return input;
  };

  const buildDirectForm = (model, instanceId, mobile) => {
    const host = create("product-form", mobile
      ? "guru-product-card__form guru-product-card__form--mobile"
      : "guru-product-card__form");
    host.dataset.guruProductCardForm = "";
    const form = create("form");
    form.method = "post";
    form.action = cartUrl("cart/add");
    form.id = `guru-product-card-form-${mobile ? "mobile" : "desktop"}-${instanceId}`;
    form.append(
      hiddenInput("quantity", model.quantityRule.minimum),
      hiddenInput("id", model.variantId),
    );
    const quantityInput = form.elements.quantity;
    quantityInput.dataset.guruQuantityMin = String(model.quantityRule.minimum);
    quantityInput.dataset.guruQuantityIncrement = String(model.quantityRule.increment);
    if (model.quantityRule.maximum !== null) {
      quantityInput.dataset.guruQuantityMax = String(model.quantityRule.maximum);
    }
    const button = create("button", mobile
      ? "guru-product-card__mobile-action"
      : "guru-product-card__quick-add");
    button.type = "submit";
    button.dataset.guruProductCardAction = "";
    button.setAttribute("aria-label", translate("addToCartWithTitle", { title: model.title }));
    if (mobile) {
      button.textContent = translate("addToCart");
    } else {
      const icon = create("span", "guru-product-card__quick-add-icon");
      icon.ariaHidden = "true";
      icon.textContent = "+";
      const label = create("span", "guru-product-card__quick-add-label");
      label.textContent = translate("addToCart");
      button.append(icon, label);
    }
    form.append(button);
    host.append(form);
    return host;
  };

  const buildLinkAction = (model, modalId, mobile) => {
    const link = create("a", mobile
      ? "guru-product-card__mobile-action"
      : "guru-product-card__quick-add");
    link.href = model.productUrl;
    const isPicker = model.available && model.oneTimeAvailable;
    const labelText = isPicker ? translate("chooseVariant") : translate("viewProduct");
    link.setAttribute(
      "aria-label",
      isPicker
        ? translate("chooseVariantWithTitle", { title: model.title })
        : translate("viewProductWithTitle", { title: model.title }),
    );
    if (isPicker) {
      link.setAttribute("aria-controls", modalId);
      link.setAttribute("aria-expanded", "false");
      link.setAttribute("aria-haspopup", "dialog");
      link.dataset.guruProductCardAction = "";
    }
    if (mobile) link.textContent = labelText;
    else {
      const icon = create("span", "guru-product-card__quick-add-icon");
      icon.ariaHidden = "true";
      icon.textContent = isPicker ? "+" : "→";
      const label = create("span", "guru-product-card__quick-add-label");
      label.textContent = labelText;
      link.append(icon, label);
    }
    return link;
  };

  const buildProductLink = (model, mobile) => {
    const link = create("a", mobile
      ? "guru-product-card__mobile-action"
      : "guru-product-card__quick-add");
    link.href = model.productUrl;
    link.setAttribute("aria-label", translate("viewProductWithTitle", { title: model.title }));
    if (mobile) link.textContent = translate("viewProduct");
    else {
      const icon = create("span", "guru-product-card__quick-add-icon");
      icon.ariaHidden = "true";
      icon.textContent = "→";
      const label = create("span", "guru-product-card__quick-add-label");
      label.textContent = translate("viewProduct");
      link.append(icon, label);
    }
    return link;
  };

  const buildSoldOutAction = (model, mobile) => {
    const button = create("button", mobile
      ? "guru-product-card__mobile-action"
      : "guru-product-card__quick-add");
    button.type = "button";
    button.disabled = true;
    button.setAttribute("aria-label", translate("soldOutWithTitle", { title: model.title }));
    if (mobile) button.textContent = translate("soldOut");
    else {
      const icon = create("span", "guru-product-card__quick-add-icon");
      icon.ariaHidden = "true";
      icon.textContent = "×";
      const label = create("span", "guru-product-card__quick-add-label");
      label.textContent = translate("soldOut");
      button.append(icon, label);
    }
    return button;
  };

  const actionFor = (model, instanceId, modalId, mobile) => {
    if (!model.available) return buildSoldOutAction(model, mobile);
    if (!model.oneTimeAvailable) return buildProductLink(model, mobile);
    if (model.variantsCount > 250) return buildProductLink(model, mobile);
    if (model.variantsCount === 1) {
      return model.quantityRule
        ? buildDirectForm(model, instanceId, mobile)
        : buildProductLink(model, mobile);
    }
    return buildLinkAction(model, modalId, mobile);
  };

  const renderCard = (viewModel, context = {}) => {
    const model = normalizeViewModel(viewModel, context);
    if (!model) throw new TypeError("Invalid Guru product-card view model");
    const cardContext = ["catalog", "search", "predictive"].includes(context.cardContext)
      ? context.cardContext
      : "catalog";
    const instanceBase = `${context.sectionId || "async"}-${model.productId}-${model.position}-${++renderedCardSequence}`
      .replace(/[^a-zA-Z0-9_-]/g, "-");
    const modalId = `guru-product-quick-buy-${instanceBase}`;
    const formattedPrice = formatMoney(model.price);
    const formattedCompare = model.compareAt === null
      ? ""
      : formatMoney(model.compareAt);
    const hideProductInformation = context.hideProductInformation === true;
    const showQuickBuy = context.showQuickBuy !== false && !hideProductInformation;

    const card = create("product-card", `product-card guru-product-card guru-product-card--${cardContext}${model.available ? "" : " is-sold-out"}`);
    card.setAttribute("handle", model.handle);
    card.dataset.guruProductCard = "";
    card.dataset.guruCardInstance = instanceBase;
    card.dataset.guruProductId = String(model.productId);
    card.dataset.guruProductTitle = model.title;
    card.dataset.guruCardMediaSource = "shopify-featured-unverified";
    card.dataset.guruCardContext = cardContext;
    card.dataset.guruMemberCurrency = model.currency;

    const figure = create("div", "product-card__figure guru-product-card__figure");
    const mediaLink = create("a", "product-card__media guru-product-card__media");
    mediaLink.href = model.productUrl;
    mediaLink.draggable = false;
    mediaLink.setAttribute("aria-label", translate("viewProductWithTitle", { title: model.title }));
    mediaLink.dataset.instant = "";

    if (!model.available || (model.compareAt !== null && model.compareAt > model.price)) {
      const badge = create(
        "span",
        `guru-product-card__badge guru-product-card__badge--${model.available ? "sale" : "sold-out"}`,
      );
      if (!model.available) badge.textContent = translate("soldOut");
      else {
        const percent = Math.round(((model.compareAt - model.price) / model.compareAt) * 100);
        badge.textContent = translate("saleBadge", { savings: `${percent}%` });
      }
      mediaLink.append(badge);
    }

    if (model.media) {
      const image = create("img", "product-card__image product-card__image--primary guru-product-card__image");
      image.src = model.media.url;
      image.alt = "";
      image.draggable = false;
      if (model.media.width) image.width = model.media.width;
      if (model.media.height) image.height = model.media.height;
      image.loading = model.position > 3 ? "lazy" : "eager";
      if (model.media.opticalScale >= 0.65 && model.media.opticalScale <= 1) {
        image.style.setProperty("--guru-product-optical-scale-mobile", model.media.opticalScale);
      }
      mediaLink.append(image);
    } else {
      const placeholder = create("span", "product-card__image product-card__image--primary guru-product-card__image guru-product-card__image--placeholder");
      placeholder.ariaHidden = "true";
      mediaLink.append(placeholder);
    }
    figure.append(mediaLink);

    const error = create("p", "guru-product-card__error");
    error.hidden = true;
    error.setAttribute("role", "alert");
    error.dataset.guruProductCardError = "";
    if (showQuickBuy) {
      const desktopAction = create("div", "guru-product-card__desktop-action");
      desktopAction.append(actionFor(model, instanceBase, modalId, false));
      figure.append(desktopAction);
    }
    figure.append(error);

    const info = create("div", "product-card__info guru-product-card__info");
    const identity = create("a", "guru-product-card__identity");
    identity.href = model.productUrl;
    identity.dataset.instant = "";
    if (context.showVendor !== false && model.vendor) {
      const vendor = create("span", "product-card__vendor guru-product-card__vendor");
      vendor.textContent = model.vendor;
      identity.append(vendor);
    }
    const title = create("h3", "product-card__title product-title guru-product-card__title");
    title.textContent = model.title;
    identity.append(title);

    const purchase = create("div", "guru-product-card__purchase");
    const priceList = create("price-list", "price-list price-list--card");
    const displayedPrice = model.priceVaries && model.priceStrategy === "from_price"
      ? translate("fromPrice", { price: formattedPrice })
      : formattedPrice;
    const salePrice = labelledMoney(
      "sale-price",
      model.compareAt === null ? "text-subdued" : "text-on-sale",
      translate("salePrice"),
      displayedPrice,
    );
    priceList.append(salePrice);
    if (model.compareAt !== null) {
      const compare = labelledMoney(
        "compare-at-price",
        "text-subdued line-through",
        translate("regularPrice"),
        formattedCompare,
      );
      priceList.append(compare);
    }
    const member = create("p", "guru-product-card__member-price");
    member.hidden = true;
    member.dataset.guruProductCardMemberPrice = "";
    member.dataset.variantId = String(model.variantId);
    member.setAttribute("aria-live", "polite");
    const memberLabel = create("span");
    memberLabel.textContent = translate("memberPrice");
    const memberValue = create("strong");
    memberValue.dataset.guruProductCardMemberPriceValue = "";
    member.append(memberLabel, document.createTextNode(" "), memberValue);
    purchase.append(priceList, member);
    if (showQuickBuy) {
      const mobileAction = create("div", "guru-product-card__mobile-action-slot");
      mobileAction.append(actionFor(model, instanceBase, modalId, true));
      purchase.append(mobileAction);
    }
    info.append(identity, purchase);
    card.append(figure);
    if (!hideProductInformation) card.append(info);

    if (
      showQuickBuy
      && model.available
      && model.oneTimeAvailable
      && model.variantsCount > 1
      && model.variantsCount <= 250
    ) {
      const modal = create("quick-buy-modal", "quick-buy-modal modal guru-quick-buy-modal");
      modal.id = modalId;
      modal.setAttribute("handle", model.handle);
      modal.setAttribute("template", "guru-product-quick-buy-template");
      modal.setAttribute("aria-label", translate("chooseVariantWithTitle", { title: model.title }));
      modal.setAttribute("initial-focus", "[data-guru-option-select], [data-guru-option-value]:not(:disabled), [data-guru-quick-buy-submit]:not(:disabled)");
      modal.dataset.guruQuickBuyInstance = instanceBase;
      modal.dataset.guruQuickBuyModal = "";
      card.append(modal);
    }
    updateExpandedWidths(card);
    return card;
  };

  const clearMemberPriceEvidence = () => {
    document.querySelectorAll("[data-guru-product-card-member-price]").forEach((row) => {
      row.hidden = true;
      const value = row.querySelector("[data-guru-product-card-member-price-value]");
      if (value) value.textContent = "";
    });
  };

  const applyMemberPriceEvidence = (evidence) => {
    const variantId = safeInteger(evidence?.variantId);
    const amount = Number(evidence?.amountMinor);
    const currency = String(evidence?.currency || "").toUpperCase();
    const valid = evidence?.authoritative === true
      && evidence?.source === MEMBER_PRICE_SOURCE
      && variantId
      && Number.isSafeInteger(amount)
      && amount >= 0
      && /^[A-Z]{3}$/.test(currency);
    if (!valid) {
      clearMemberPriceEvidence();
      return false;
    }

    let applied = false;
    document.querySelectorAll("[data-guru-product-card-member-price]").forEach((row) => {
      const card = row.closest(CARD_SELECTOR);
      const matches = safeInteger(row.dataset.variantId) === variantId
        && String(card?.dataset.guruMemberCurrency || "").toUpperCase() === currency;
      row.hidden = !matches;
      const value = row.querySelector("[data-guru-product-card-member-price-value]");
      if (value) {
        value.textContent = matches
          ? formatMoney(amount)
          : "";
      }
      applied ||= matches;
    });
    return applied;
  };

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest(`${ACTION_SELECTOR}[aria-controls]`);
    if (!trigger || !trigger.closest(CARD_SELECTOR)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void openQuickBuy(trigger);
  }, true);

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void submitProductCardForm(form);
  }, true);

  document.addEventListener("cart:error", (event) => {
    const card = event.target.closest?.(CARD_SELECTOR);
    if (!card || event.target.closest?.(MODAL_SELECTOR)) return;
    setCardError(card, event.detail?.error || translate("cartAddError"));
  }, true);

  document.addEventListener("variant:add", (event) => {
    const card = event.target.closest?.(CARD_SELECTOR);
    if (card) setCardError(card);
  }, true);

  document.addEventListener(PREDICTIVE_INVALIDATE_EVENT, (event) => {
    const searchRoot = event.detail?.root instanceof Element
      ? event.detail.root
      : event.target?.closest?.('[data-guru-panel="search"], [data-component-overlay="search"]');
    predictiveQuickBuyOperations.get(searchRoot)?.cancel({
      focusSearchInput: event.detail?.reason === "replacement",
    });
  });

  document.addEventListener("guru:product-card:member-price", (event) => {
    applyMemberPriceEvidence(event.detail);
  });
  document.addEventListener("guru:product-card:member-price-clear", clearMemberPriceEvidence);

  document.addEventListener("shopify:section:load", (event) => updateExpandedWidths(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const modalIds = new Set(
      Array.from(event.target.querySelectorAll?.(`${ACTION_SELECTOR}[aria-controls]`) || [])
        .map((control) => control.getAttribute("aria-controls"))
        .filter(Boolean),
    );
    modalIds.forEach((id) => {
      const modal = document.getElementById(id);
      if (!modal?.matches(MODAL_SELECTOR)) return;
      const operation = quickBuyOperations.get(modal);
      if (operation?.cancel) operation.cancel();
      else modal.cancelPendingShow?.();
      modal.remove();
    });
  });

  const initialize = (scope = document) => updateExpandedWidths(scope);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initialize(document), { once: true });
  else initialize(document);

  window.GuruProductCard = {
    initialized: true,
    initialize,
    openQuickBuy,
    renderCard,
    applyMemberPriceEvidence,
    clearMemberPriceEvidence,
  };

  window.GuruCollectionSearchAdapter = {
    async applyResults({ root, payload, commit } = {}) {
      if (!(root instanceof Element) || !Array.isArray(payload?.products) || typeof commit !== "function") {
        return false;
      }
      const context = {
        sectionId: root.dataset.guruCollectionSectionId || "collection-search",
        locale: root.dataset.guruCollectionLanguage || config.locale,
        currency: root.dataset.guruCollectionCurrency || config.currency,
        showVendor: true,
      };
      const identities = new Set();
      payload.products.forEach((product) => {
        const productId = safeInteger(product?.id);
        const variantId = safeInteger(product?.selected_variant?.id);
        const handle = typeof product?.handle === "string" ? product.handle : "";
        if (!productId || !variantId || !handle) {
          throw new TypeError("Invalid Guru collection-search identity");
        }
        [`product:${productId}`, `variant:${variantId}`, `handle:${handle}`].forEach((identity) => {
          if (identities.has(identity)) throw new TypeError("Duplicate Guru collection-search product");
          identities.add(identity);
        });
      });
      const cards = payload.products.map((product, index) => renderCard(product, {
        ...context,
        position: Number(product.position) || index + 1,
      }));
      const committed = await commit({ cards, payload });
      if (committed === false) return false;
      const nextRoot = committed instanceof Element ? committed : committed?.root;
      updateExpandedWidths(nextRoot || root);
      return nextRoot ? { root: nextRoot } : { root };
    },
  };
})();
